/*************************************************************************
 *
 * c20g CONFIDENTIAL
 * __________________
 *
 *  [2007] - [2023] Counterpoint Consulting, Incorporated
 *  All Rights Reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Counterpoint Consulting Incorporated.
 * The intellectual and technical concepts contained
 * herein are proprietary to Counterpoint Consulting Incorporated
 * and its suppliers and may be covered by U.S. and Foreign Patents,
 * patents in process, and are protected by trade secret or copyright law.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Counterpoint Consulting Incorporated.
 */

import { View } from 'backbone.marionette';
import Syphon from 'backbone.syphon';
import Backbone from 'backbone';
import Ladda from 'ladda';
import _ from 'lodash';

import { formatDisplayDate } from '@/util/MomentUtils';
import PhoneFormatBehavior from '@/behaviors/PhoneFormatBehavior';
import showErrorMessage from '@/util/showErrorMessage';
import showSuccessMessage from '@/util/ShowSuccessMessage';
import showModal from '@/util/showModal';
import ConfirmationModalView from '@/views/ConfirmationModalView';
import TooltipView from '@/views/TooltipView';

import CalculateContractNotificationDate from '../../services/CalculateContractNotificationDate';
import CommandPOCsGrid from '../CommandPOCsGrid/CommandPOCsGrid';

import tmplExpiringContractNotificationModal from './ExpiringContractNotificationModal.hbs';

const ExpiringContractNotificationModal = View.extend({
  template: tmplExpiringContractNotificationModal,

  className: 'expiring-contract-notification-modal-view',

  ui: {
    saveBtn: '.saveBtn',
    expiringContractNotificationForm: '#expiringContractNotificationForm',
    commandPOCsGrid: '#commandPOCsGrid',
    addContractVehicleBtn: '.add-contract-vehicle',
    contractVehicleSelect: '.contract-vehicle',
    calculateDates: '.calculate-dates',
    notificationDate: '#notificationDate',
    contractVehicleName: '#contractVehicleName',
    contractVehicleTooltip: '#contractVehicleTooltip'
  },

  regions: {
    commandPOCsGrid: '@ui.commandPOCsGrid',
    contractVehicleTooltip: '@ui.contractVehicleTooltip'
  },

  events: {
    'click @ui.saveBtn': 'onClickSave',
    'click @ui.addContractVehicleBtn': 'onClickEditContractVehicle',
    'click @ui.calculateDates': 'onClickCalculateDates'
  },

  behaviors: {
    telephone: PhoneFormatBehavior
  },

  templateContext() {
    return {
        contractEndDate: formatDisplayDate(this.model.get('contractEndDate')),
        orderFormTypes: this.options.orderFormTypes,
        showNotificationDateInvalid: !this.model.get('notificationDate') && this.model.get('contractVehicleName')
    };
  },

  async onRender() {
    if(this.model.get('contractVehicleName')) {
      this.ui.addContractVehicleBtn.hide();
    } else {
      this.ui.contractVehicleName.hide();
    }
    this.ui.contractVehicleSelect.hide();
    this.ui.calculateDates.hide();

    this.commandPOCsGrid = new CommandPOCsGrid({
        model: new Backbone.Model({
          commandPOCs: this.model.get('commandPOCs')
        })
      });
    this.showChildView('commandPOCsGrid', this.commandPOCsGrid);

    this.showChildView(
      'contractVehicleTooltip',
      new TooltipView({
        model: new Backbone.Model({
          tooltipText:
            'The following contract vehicles can have notification dates calculated: PRTNR A3, PRNTR A4, PRTNR Production, Japan IDIQ, RFQ, and Maintenance.',
          placement: 'right'
        })
      })
    );
  },

  async onClickEditContractVehicle(e) {
    e?.preventDefault();
    this.ui.addContractVehicleBtn.hide();
    this.ui.contractVehicleSelect.show();
    this.ui.calculateDates.show();
  },

  async onClickCalculateDates(e) {
    e?.preventDefault();
    const spinner = Ladda.create(e.currentTarget);

    try {
      spinner.start();
      const formData = Syphon.serialize(this);

      if(formData.orderTypeId.length > 0) {
        this.contractNotificationData = await CalculateContractNotificationDate({
          contractVehicle: formData.orderTypeId,
          piinSpiin: this.model.get('piinSpiin'),
          numberDevices: this.model.get('numberDevices'),
          contractTotalValue: this.model.get('totalValue')
        });
        this.updateModalData();
      } else {
        this.showNoContractVehicleSelectedError();
      }
    } catch {
      this.showCalculationError();
    } finally {
      if(!this.contractNotificationData.notificationDate) this.showInvalidContractVehicleTypeError();
      spinner.stop();
    }
  },

  updateModalData() {
    this.contractNotificationData.notificationDate 
    ? this.ui.notificationDate.text(formatDisplayDate(this.contractNotificationData.notificationDate))
    : this.ui.notificationDate.text('Notification Start Date could not be calculated for this contract vehicle.').addClass('fst-italic');

    this.ui.contractVehicleName.show().text(this.contractNotificationData.contractVehicleName);
    this.model.set('contractVehicleName', this.contractNotificationData.contractVehicleName);
    this.ui.addContractVehicleBtn.hide();
    this.ui.contractVehicleSelect.hide();
    this.ui.calculateDates.hide();
  },

  async onClickSave() {
    const selectedPOCs = this.commandPOCsGrid.viewGrid.getSelectedModels();
    if(selectedPOCs.length < 1) {
      this.noPocSelectedError();
    } else {
      const missingData = [];
      _.each(selectedPOCs, (poc) => {
        if(!poc.attributes.pocName || !poc.attributes.email) {
          missingData.push(poc)
        }
      })

      if (missingData.length > 0) {
        this.showPOCMissingDataError();
      } else {
        const formData = this.formatFormData(selectedPOCs);
        await this.showConfirmationModal(formData.pocsString);

        this.triggerMethod('modal:close', formData);
      }
    }
  },

  formatFormData(selectedPOCs) {
    const formattedPOCs = [];
    const pocsString = [];

    _.each(selectedPOCs, (poc) => {
      poc = {
        commandId: poc.attributes.commandId,
        pocName: poc.attributes.pocName,
        email: poc.attributes.email,
        telephone: poc.attributes.telephone
      }
      formattedPOCs.push(poc)
      pocsString.push(`${poc.pocName} (${poc.email})`)
    })

    const formData = {
      piinSpiin: this.model.get('piinSpiin'),
      contractEndDate: formatDisplayDate(this.model.get('contractEndDate')),
      contractVehicleName: this.model.get('contractVehicleName'),
      formattedPOCs,
      pocsString: pocsString.join(', ')
    }
    
    return formData;
  },

  async showConfirmationModal(pocsString) {
    await showModal({
      title: 'Send Funding Renewal Notifications',
      view: new ConfirmationModalView({
        confirmText: 'Send',
        confirmButtonClass: 'btn-primary',
        model: new Backbone.Model({
          message: `An email will be sent to the following contacts: ${pocsString}. Are you sure you want to send expiring contract emails?`,
          async onConfirm() {
            return Promise.resolve();
          }
        })
      })
    });
  },

  showNoContractVehicleSelectedError() {
    showErrorMessage({
      errorCode: 'calculationError',
      errorTitle: 'Calculation Error',
      errorMessage: 'A contract vehicle must be selected in order to calculate the notification date. If a contract vehicle is selected and you receive this error, please contact an administrator.'
    });
  },

  showInvalidContractVehicleTypeError() {
    showErrorMessage({
      errorCode: 'calculationError',
      errorTitle: 'Calculation Error',
      errorMessage: 'The notification date could not be calculated for the contract vehicle selected.'
    });
  },

  showCalculationError() {
    showErrorMessage({
      errorCode: 'calculationError',
      errorTitle: 'Calculation Error',
      errorMessage: 'An error occurred during calculation. Try again or contact an administrator if your issue persists.'
    });
  },

  showCalculationSuccessMessage() {
    showSuccessMessage({
      code: 'calculationSuccess',
      title: 'Calculation Success',
      message: 'The notification date for this contract has been calculated. This contract has now been updated to include the contract vehicle.'
    })
  },

  noPocSelectedError() {
    showErrorMessage({
      errorCode: 'pocSelectionError',
      errorTitle: 'POC Selection Error',
      errorMessage: 'At least one point of contact must be selected in order to send notification(s).'
    });
  },

  showPOCMissingDataError() {
    showErrorMessage({
      errorCode: 'pocDataError',
      errorTitle: 'Missing Contact Information Error',
      errorMessage: 'One or more of the selected rows do not include contact information. Please add contact information to the row(s) to continue.'
    });
  }
});

export default ExpiringContractNotificationModal;
